import Button from 'ext-ui/components/Button/Button'
import Pill from 'ext-ui/components/Pill/Pill'
import React, { useState } from 'react'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import {
  formatStatementAmount,
  formatStatementDateShort,
  formatStatementPeriod,
} from 'src/page-components/Accounts/AccountStatementsTab/accountStatements.format'
import {
  isStatementPaid,
  STATEMENT_DOCUMENT_FORMATS,
  type StatementSummary,
} from 'src/page-components/Accounts/AccountStatementsTab/accountStatementsDemoData'
import StatementDetailModal from 'src/page-components/Accounts/AccountStatementsTab/components/StatementDetailModal/StatementDetailModal'
import StatementExportMenu from 'src/page-components/Accounts/AccountStatementsTab/components/StatementExportMenu/StatementExportMenu'
import * as style from 'src/page-components/Accounts/AccountStatementsTab/components/StatementListRow/StatementListRow.style'
import {
  getHttpsStatementDocumentUrl,
  useStatementDownload,
} from 'src/page-components/Accounts/AccountStatementsTab/useStatementDownload'
import {
  statementStatusPillColor,
  useStatementStatusLabel,
} from 'src/page-components/Accounts/AccountStatementsTab/useStatementLabels'

interface StatementListRowProps {
  statement: StatementSummary
  /** Card label passed through to the detail modal's description. */
  cardLabel: string
  /** The card whose line items the detail modal reads. */
  creditCardId: string | undefined
  /** True when `statement` is the fabricated fallback rather than a real read. */
  isDemoStatement: boolean
  /**
   * Set on the first row revealed by "See more statements" so the list can move
   * focus here — the control the user activated unmounts itself.
   */
  viewButtonRef?: React.Ref<HTMLButtonElement>
}

/**
 * One statement row in the card-detail Statements list (EX-40600).
 *
 * Current and paid statements share one anatomy — period, owed/due-or-paid
 * detail, status, amount, View — and only the status and the detail copy change.
 * Keeping them identical is a design guardrail, so resist splitting this into
 * two row components.
 *
 * The row owns its own detail-open state; nothing above it tracks which
 * statement is open.
 *
 * Deviation from the design prototype, deliberate: the prototype also makes the
 * whole row clickable as a mouse convenience via a `<div onClick>`. That is
 * banned by `src/CLAUDE.md` ("No `<div onClick>`"), and the prototype itself
 * treats View as the real accessible trigger, so View is the only trigger here.
 * No keyboard or pointer affordance is lost.
 *
 * Download (EX-40602) follows View. Card Management publishes the available
 * document URLs on each statement, so the row offers only configured formats.
 */
const StatementListRow: React.FC<StatementListRowProps> = ({
  statement,
  cardLabel,
  creditCardId,
  isDemoStatement,
  viewButtonRef,
}) => {
  const translate = useTranslate()
  const formatCurrency = useCurrencyFormatter()
  const [isDetailOpen, setIsDetailOpen] = useState(false)
  const { download } = useStatementDownload(statement)

  const period = formatStatementPeriod(statement.period)
  const isPaid = isStatementPaid(statement.status)
  const statusLabel = useStatementStatusLabel(statement.status)
  const owed = formatStatementAmount(formatCurrency, statement.amountOwed)
  const availableDocumentFormats = STATEMENT_DOCUMENT_FORMATS.filter((format) =>
    Boolean(getHttpsStatementDocumentUrl(statement, format)),
  )

  const detail = isPaid
    ? translate('Owed ${owed} · Paid ${paid}', {
        owed,
        paid: formatStatementAmount(
          formatCurrency,
          statement.amountPaid ?? statement.amountOwed,
        ),
      })
    : translate('Owed ${owed} · Payment due ${dueOn}', {
        owed,
        dueOn: formatStatementDateShort(statement.dueOn),
      })

  return (
    <li css={style.row} data-testid="statementListRow">
      <div css={style.rowContent}>
        <span css={style.period}>
          <span css={style.periodTitle}>{period}</span>
          <span css={style.periodDetail}>{detail}</span>
        </span>
        <span css={style.rowActions}>
          {/* Status text is visible, never colour-only. */}
          <Pill color={statementStatusPillColor(statement.status)} size="small">
            {statusLabel}
          </Pill>
          <span css={style.amount}>
            {formatStatementAmount(formatCurrency, statement.statementAmount)}
          </span>
          <Button
            ref={viewButtonRef}
            variant="text"
            aria-haspopup="dialog"
            // Each row's control needs a distinct accessible name — a list of
            // identical "View" buttons is unusable from a screen reader's
            // element list.
            aria-label={translate('View ${period} statement', { period })}
            onClick={() => setIsDetailOpen(true)}
          >
            {translate('View')}
          </Button>
          <StatementExportMenu
            availableFormats={availableDocumentFormats}
            period={period}
            onSelect={download}
          />
        </span>
      </div>
      {/*
        Mounted only while open. `open` is a prop, not a render guard, so
        leaving it always-mounted made every row build its full ledger — ~24
        currency formats each, plus a permanent react-modal portal node — for
        eight statements that are almost always closed. react-modal returns
        focus to the trigger on unmount, so View still regains focus on close.
      */}
      {isDetailOpen && (
        <StatementDetailModal
          open
          onClose={() => setIsDetailOpen(false)}
          statement={statement}
          cardLabel={cardLabel}
          creditCardId={creditCardId}
          isDemoStatement={isDemoStatement}
        />
      )}
    </li>
  )
}

export default StatementListRow
